/**
 * Pipeline robusta para o fluxo de cálculo de orçamento.
 * 
 * Integra todas as etapas com retry automático e tratamento robusto de erros:
 * 0. ✅ PRÉ-RENOVAÇÃO: Garantir token válido ANTES de iniciar (igual OneBitmap)
 * 1. Carregar regras da API (com retry e tratamento de 401)
 * 2. Coletar objetos da seleção (com retry)
 * 3. Detectar material baseado no maior objeto
 * 4. Criar snapshot para OrderDetailsWindow
 * 
 * ✅ CARACTERÍSTICAS:
 * - PRÉ-RENOVAÇÃO de token (evita 401 no primeiro request)
 * - Retry automático com backoff exponencial
 * - Tratamento de erros 401 (token expirado)
 * - Validação de resultados em cada etapa
 * - Logs detalhados para debugging
 * - Suporte a abortSignal para cancelamento
 * - Métricas de tempo por etapa
 */

import { sendToTerminal } from '../../../../lib/terminalLogger';
import { LogCategory } from '../../../../config/logging';
import { convertToOrderObjects } from '../../../../lib/corelOperations';
import {
  MetadataService,
  NoActiveDocumentError,
  NoSelectionError,
  MetadataGenericError,
  type MetadataProgress
} from '../../../../lib/corel/actions/metadata';
import { writeSnapshot, setPreparedPipelineId } from '../../../../lib/corel/pipeline/pipelineKeys';
import { PIPELINE_TTL } from '../../../../lib/corel/pipeline/pipelineConstants';
import { executePipelineStepWithRetry } from '../../../../lib/corel/pipeline/pipelineRetry';
import { fetchProductRules } from './rulesService';
import { detectMaterial } from './materialDetection';
import { ensureFreshToken } from '../../../../lib/auth';
import type { ProductRule, OrderObject, MaterialDetectionResult } from '../types';

// IDs fixos para o orçamento
const QUOTATION_PEDIDO_ID = 'quotation';
const QUOTATION_ITEM_ID = 'quotation-item';

export interface QuotationFlowPipelineOptions {
  /** Sinal de abort para cancelar a pipeline */
  abortSignal?: AbortSignal | null;
  /** Callback de progresso por etapa */
  onProgress?: (event: {
    stage: string;
    meta?: Record<string, unknown>;
    retryAttempt?: number;
    maxRetries?: number;
  }) => void;
  /** Callback de progresso detalhado durante coleta de metadados (C# progress_update) */
  onCollectProgress?: (progress: MetadataProgress) => void;
}

export interface QuotationFlowPipelineResult {
  status: 'success' | 'error' | 'cancelled';
  error?: { code: string; message?: string };
  data?: {
    rules: ProductRule[];
    objects: OrderObject[];
    detectionResult: MaterialDetectionResult | null;
    pipelineId: string;
  };
  metrics?: {
    rulesLoadMs?: number;
    collectMs?: number;
    detectionMs?: number;
    totalMs?: number;
  };
  pipelineId?: string;
}

/**
 * Executa a pipeline completa de cálculo de orçamento.
 */
export async function runQuotationFlowPipeline(
  options: QuotationFlowPipelineOptions = {}
): Promise<QuotationFlowPipelineResult> {
  const startedAt = Date.now();
  const pipelineId = `quotation-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const emit = (stage: string, meta?: Record<string, unknown>) => {
    try {
      if (typeof options.onProgress === 'function') {
        options.onProgress({ stage, meta });
      }
    } catch {}
  };

  sendToTerminal('━━━━━━ [QUOTATION_FLOW] INÍCIO ━━━━━━', {
    pipelineId
  }, LogCategory.BUSINESS);
  emit('start', { pipelineId });

  // Verificar abortSignal antes de iniciar
  if (options.abortSignal?.aborted) {
    sendToTerminal('❌ [QUOTATION_FLOW] ABORTADO antes de iniciar', { pipelineId }, LogCategory.ERROR);
    return {
      status: 'cancelled',
      error: { code: 'aborted' },
      metrics: { totalMs: Date.now() - startedAt },
      pipelineId
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ETAPA 0: PRÉ-RENOVAÇÃO DE TOKEN (igual OneBitmap/MontaTec)
  // ═══════════════════════════════════════════════════════════════════════════
  emit('token:checking');
  
  try {
    await ensureFreshToken();
    emit('token:valid');
  } catch (tokenError) {
    const errorMsg = tokenError instanceof Error ? tokenError.message : 'Erro de autenticação';
    
    sendToTerminal('❌ [QUOTATION_FLOW:0] Falha na pré-renovação de token', {
      pipelineId,
      error: errorMsg,
      durationMs: Date.now() - startedAt
    }, LogCategory.ERROR);
    emit('token:error', { error: errorMsg });
    
    return {
      status: 'error',
      error: { code: 'auth_failed', message: errorMsg },
      metrics: { totalMs: Date.now() - startedAt },
      pipelineId
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ETAPA 1: CARREGAR REGRAS (COM RETRY)
  // ═══════════════════════════════════════════════════════════════════════════
  sendToTerminal('📋 [QUOTATION_FLOW:1] Carregando regras de produtos...', { pipelineId });
  const t1 = Date.now();
  emit('rules:loading');

  let rules: ProductRule[] = [];
  let rulesLoadMs: number;

  try {
    let retryAttempt = 0;
    const retryResult = await executePipelineStepWithRetry(
      async () => {
        retryAttempt++;
        // ✅ PADRONIZADO: Usar fetchProductRules (já usa cache via api() helper)
        // Não precisa limpar cache manualmente - api() helper gerencia isso
        return await fetchProductRules();
      },
      {
        maxRetries: 3,
        baseDelayMs: 1000,
        maxDelayMs: 5000,
        backoffFactor: 2,
        // Lista vazia é resultado VÁLIDO (nenhum material habilitado p/ orçamento):
        // o backend retorna 200 [] e o fluxo segue até a tela de seleção (que
        // mostra o estado vazio). Só um retorno não-array é falha de verdade.
        validateResult: (result) => Array.isArray(result),
        abortSignal: options.abortSignal,
        stepName: 'carregar-regras',
        pipelineId,
        onRetry: (attempt, error) => {
          emit('rules:retry', { 
            attempt, 
            maxRetries: 3,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }
    );

    rules = retryResult.result;
    rulesLoadMs = retryResult.totalMs;

    sendToTerminal('✅ [QUOTATION_FLOW:1] Regras carregadas com sucesso', {
      pipelineId,
      rulesCount: rules.length,
      durationMs: rulesLoadMs,
      attempts: retryResult.attempts
    }, LogCategory.BUSINESS);
    emit('rules:loaded', { count: rules.length, durationMs: rulesLoadMs });

  } catch (error) {
    rulesLoadMs = Date.now() - t1;
    
    // ✅ Mensagens de erro específicas e amigáveis
    let errorMsg = 'Falha ao carregar regras de produtos';
    let errorCode = 'rules_load_failed';
    
    if (error instanceof Error) {
      const originalMsg = error.message.toLowerCase();
      
      // Detectar tipo específico de erro
      if (originalMsg.includes('timeout') || originalMsg.includes('tempo limite')) {
        errorMsg = 'Tempo limite excedido ao buscar materiais. Verifique sua conexão com a internet e tente novamente.';
        errorCode = 'rules_load_timeout';
      } else if (originalMsg.includes('401') || originalMsg.includes('token') || originalMsg.includes('autorização')) {
        errorMsg = 'Sessão expirada. Por favor, faça login novamente.';
        errorCode = 'rules_load_auth';
      } else if (originalMsg.includes('network') || originalMsg.includes('fetch') || originalMsg.includes('conexão')) {
        errorMsg = 'Erro de conexão com o servidor. Verifique sua internet e tente novamente.';
        errorCode = 'rules_load_network';
      } else if (originalMsg.includes('array') || originalMsg.includes('vazio') || originalMsg.includes('nenhuma regra')) {
        errorMsg = 'Nenhuma regra de material encontrada. Verifique as configurações do sistema.';
        errorCode = 'rules_load_empty';
      } else {
        errorMsg = `Erro ao carregar materiais: ${error.message}`;
      }
    }

    sendToTerminal('❌ [QUOTATION_FLOW:1] Falha ao carregar regras', {
      pipelineId,
      durationMs: rulesLoadMs,
      error: errorMsg,
      errorCode
    }, LogCategory.ERROR);
    emit('rules:error', { error: errorMsg, errorCode });

    return {
      status: 'error',
      error: { code: errorCode, message: errorMsg },
      metrics: { rulesLoadMs, totalMs: Date.now() - startedAt },
      pipelineId
    };
  }

  // Verificar abortSignal antes de coletar objetos
  if (options.abortSignal?.aborted) {
    sendToTerminal('❌ [QUOTATION_FLOW] ABORTADO após carregar regras', { pipelineId }, LogCategory.ERROR);
    return {
      status: 'cancelled',
      error: { code: 'aborted' },
      metrics: { rulesLoadMs, totalMs: Date.now() - startedAt },
      pipelineId
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ETAPA 2: COLETAR OBJETOS DA SELEÇÃO (COM RETRY)
  // ═══════════════════════════════════════════════════════════════════════════
  sendToTerminal('🔍 [QUOTATION_FLOW:2] Coletando objetos da seleção...', { pipelineId });
  const t2 = Date.now();
  emit('collect:loading');

  let objects: OrderObject[] = [];
  let rawObjects: any[] = []; // ✅ Manter objetos brutos para snapshot
  let collectMs: number;

  try {
    const retryResult = await executePipelineStepWithRetry(
      async () => {
        // ✅ MetadataService.collect() já lança erros customizados (NoSelectionError, etc)
        // Não precisamos validar aqui - deixar o erro ser propagado
        // ✅ ATUALIZADO: Passar callback de progresso para MetadataService
        const response = await MetadataService.collect({
          scope: 'selection',
          onProgress: (progress) => {
            // Emitir evento de progresso de coleta
            emit('collect:progress', {
              percentComplete: progress.percentComplete,
              current: progress.current,
              total: progress.total,
              message: progress.message
            });
            // Chamar callback específico se fornecido
            if (options.onCollectProgress) {
              options.onCollectProgress(progress);
            }
          }
        });

        // ✅ Se chegou aqui, a resposta foi bem-sucedida
        // Converter objetos
        const converted = convertToOrderObjects(response.objetos as any, 'mm');
        return { response, objects: converted, rawObjects: response.objetos };
      },
      {
        maxRetries: 3,
        baseDelayMs: 1000,
        maxDelayMs: 5000,
        backoffFactor: 2,
        validateResult: (result) => {
          return result?.objects && Array.isArray(result.objects) && result.objects.length > 0;
        },
        // ✅ CORRIGIDO: Não fazer retry para erros que não podem ser resolvidos (NO_SELECTION, NO_ACTIVE_DOCUMENT)
        shouldRetry: (error) => {
          // Erros de seleção/documento não devem ser retentados - não vão aparecer seleção em 1 segundo
          if (error instanceof NoSelectionError || error instanceof NoActiveDocumentError) {
            return false; // Não retry
          }
          // Outros erros podem ser retentados
          return true;
        },
        abortSignal: options.abortSignal,
        stepName: 'coletar-objetos',
        pipelineId,
        onRetry: (attempt, error) => {
          emit('collect:retry', { 
            attempt, 
            maxRetries: 3,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }
    );

    objects = retryResult.result.objects;
    rawObjects = retryResult.result.rawObjects;
    collectMs = retryResult.totalMs;

    sendToTerminal('✅ [QUOTATION_FLOW:2] Objetos coletados com sucesso', {
      pipelineId,
      objetosCount: objects.length,
      durationMs: collectMs,
      attempts: retryResult.attempts,
      primeiroObjeto: objects[0] ? {
        nome: objects[0].nome,
        width: objects[0].width,
        height: objects[0].height
      } : null
    }, LogCategory.BUSINESS);
    emit('collect:loaded', { count: objects.length, durationMs: collectMs });

  } catch (error) {
    collectMs = Date.now() - t2;
    
    // ✅ Tratar erros customizados do MetadataService
    let errorMsg = 'Falha ao coletar objetos da seleção';
    let errorCode = 'collect_failed';
    
    if (error instanceof NoActiveDocumentError) {
      errorMsg = error.message;
      errorCode = error.code;
      sendToTerminal('❌ [QUOTATION_FLOW:2] Documento não ativo', {
        pipelineId,
        durationMs: collectMs,
        errorCode,
        errorMessage: errorMsg,
        originalMessage: error.originalMessage
      }, LogCategory.ERROR);
    } else if (error instanceof NoSelectionError) {
      errorMsg = error.message;
      errorCode = error.code;
      sendToTerminal('❌ [QUOTATION_FLOW:2] Nenhuma seleção detectada', {
        pipelineId,
        durationMs: collectMs,
        errorCode,
        errorMessage: errorMsg,
        originalMessage: error.originalMessage
      }, LogCategory.ERROR);
    } else if (error instanceof MetadataGenericError) {
      errorMsg = error.message;
      errorCode = error.code;
      sendToTerminal('❌ [QUOTATION_FLOW:2] Erro genérico ao coletar metadados', {
        pipelineId,
        durationMs: collectMs,
        errorCode,
        errorMessage: errorMsg,
        originalMessage: error.originalMessage
      }, LogCategory.ERROR);
    } else if (error instanceof Error) {
      const originalMsg = error.message.toLowerCase();
      
      // Detectar outros tipos de erro (timeout, etc)
      if (originalMsg.includes('timeout') || originalMsg.includes('tempo limite')) {
        errorMsg = 'Tempo limite excedido ao coletar objetos. O CorelDRAW pode estar ocupado. Tente novamente.';
        errorCode = 'collect_timeout';
      } else if (originalMsg.includes('corel') || originalMsg.includes('não respondeu')) {
        errorMsg = 'O CorelDRAW não respondeu. Verifique se o programa está aberto e funcionando corretamente.';
        errorCode = 'collect_corel_unresponsive';
      } else {
        errorMsg = `Erro ao coletar objetos: ${error.message}`;
      }
      
      sendToTerminal('❌ [QUOTATION_FLOW:2] Erro ao coletar objetos', {
        pipelineId,
        durationMs: collectMs,
        error: errorMsg,
        errorCode
      }, LogCategory.ERROR);
    } else {
      sendToTerminal('❌ [QUOTATION_FLOW:2] Erro desconhecido ao coletar objetos', {
        pipelineId,
        durationMs: collectMs,
        error: String(error)
      }, LogCategory.ERROR);
    }

    emit('collect:error', { error: errorMsg, errorCode });

    return {
      status: 'error',
      error: { code: errorCode, message: errorMsg },
      metrics: { rulesLoadMs, collectMs, totalMs: Date.now() - startedAt },
      pipelineId
    };
  }

  // Verificar abortSignal antes de detectar material
  if (options.abortSignal?.aborted) {
    sendToTerminal('❌ [QUOTATION_FLOW] ABORTADO após coletar objetos', { pipelineId }, LogCategory.ERROR);
    return {
      status: 'cancelled',
      error: { code: 'aborted' },
      metrics: { rulesLoadMs, collectMs, totalMs: Date.now() - startedAt },
      pipelineId
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ETAPA 3: CRIAR SNAPSHOT
  // ═══════════════════════════════════════════════════════════════════════════
  sendToTerminal('💾 [QUOTATION_FLOW:3] Criando snapshot...', {
    pipelineId,
    objetosCount: objects.length
  });
  emit('snapshot:creating');

  try {
    // ✅ Usar objetos brutos da resposta do MetadataService
    if (!rawObjects || rawObjects.length === 0) {
      throw new Error('Objetos brutos não disponíveis para snapshot');
    }

    const payload = {
      meta: {
        docKey: null,
        capturedAt: Date.now(),
        total: rawObjects.length
      },
      objetos: rawObjects
    };

    writeSnapshot(pipelineId, payload, PIPELINE_TTL.SNAPSHOT_EXTENDED);
    setPreparedPipelineId(QUOTATION_PEDIDO_ID, QUOTATION_ITEM_ID, pipelineId);

    sendToTerminal('✅ [QUOTATION_FLOW:3] Snapshot criado com sucesso', {
      pipelineId,
      objetosCount: objects.length
    }, LogCategory.BUSINESS);
    emit('snapshot:created', { count: objects.length });

  } catch (snapshotError) {
    sendToTerminal('⚠️ [QUOTATION_FLOW:3] Erro ao criar snapshot (não crítico)', {
      pipelineId,
      error: snapshotError instanceof Error ? snapshotError.message : String(snapshotError)
    }, LogCategory.ERROR);
    emit('snapshot:error', {
      error: snapshotError instanceof Error ? snapshotError.message : String(snapshotError)
    });
    // Não falhar o fluxo por erro de snapshot
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ETAPA 4: DETECTAR MATERIAL (ALGORITMO MELHORADO)
  // ═══════════════════════════════════════════════════════════════════════════
  sendToTerminal('🔍 [QUOTATION_FLOW:4] Detectando material...', {
    pipelineId,
    objetosCount: objects.length,
    rulesCount: rules.length
  });
  const t3 = Date.now();
  emit('detection:starting');

  let detectionResult: MaterialDetectionResult | null = null;
  let detectionMs: number;

  try {
    // ✅ ALGORITMO MELHORADO:
    // 1. Encontra o maior objeto (por dimensão máxima)
    // 2. Ordena regras por área (menor → maior) para otimizar custo
    // 3. Encontra a PRIMEIRA regra onde o objeto CABE (com tolerância de 2cm)
    // 4. Verifica com rotação (objeto pode caber rotacionado 90°)
    // 5. Calcula confiança baseada na "folga" disponível
    detectionResult = detectMaterial(objects, rules);

    if (detectionResult) {
      sendToTerminal('✅ [QUOTATION_FLOW:4] Material detectado', {
        pipelineId,
        material: detectionResult.rule.produto?.nome_servico,
        confianca: `${detectionResult.confidence}%`,
        maiorObjeto: {
          nome: detectionResult.largestObject.nome,
          largura: detectionResult.largestObject.width.toFixed(2),
          altura: detectionResult.largestObject.height.toFixed(2)
        }
      }, LogCategory.BUSINESS);
      emit('detection:success', {
        material: detectionResult.rule.produto?.nome_servico,
        confidence: detectionResult.confidence
      });
    } else {
      sendToTerminal('⚠️ [QUOTATION_FLOW:4] Nenhum material compatível', {
        pipelineId,
        rulesCount: rules.length,
        objetosCount: objects.length
      }, LogCategory.BUSINESS);
      emit('detection:no_match');
    }

    detectionMs = Date.now() - t3;

  } catch (error) {
    detectionMs = Date.now() - t3;
    const errorMsg = error instanceof Error
      ? error.message
      : 'Falha ao detectar material';

    sendToTerminal('⚠️ [QUOTATION_FLOW:4] Erro na detecção (não crítico)', {
      pipelineId,
      durationMs: detectionMs,
      error: errorMsg
    }, LogCategory.ERROR);
    emit('detection:error', { error: errorMsg });
    // Não falhar o fluxo por erro de detecção (usuário pode selecionar manualmente)
  }

  const totalMs = Date.now() - startedAt;

  sendToTerminal('━━━━━━ [QUOTATION_FLOW] SUCESSO ━━━━━━', {
    pipelineId,
    rulesCount: rules.length,
    objetosCount: objects.length,
    materialDetectado: detectionResult ? detectionResult.rule.produto?.nome_servico : null,
    metrics: { rulesLoadMs, collectMs, detectionMs, totalMs }
  }, LogCategory.BUSINESS);
  emit('end', { ok: true });

  return {
    status: 'success',
    data: {
      rules,
      objects,
      detectionResult,
      pipelineId
    },
    metrics: {
      rulesLoadMs,
      collectMs,
      detectionMs,
      totalMs
    },
    pipelineId
  };
}

